import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../../components/SEO";

const processSteps = [
  {
    id: "01",
    title: "Brief & Requirement Mapping",
    tag: "Discovery",
    description:
      "We understand label size, substrate, finish, application surface, and volume expectations before we lock production direction.",
    points: [
      "Commercial and technical requirement review",
      "Material and finish suitability mapping",
      "Turnaround and quantity planning",
    ],
  },
  {
    id: "02",
    title: "Artwork Engineering",
    tag: "Prepress",
    description:
      "Artwork is refined for print feasibility, color accuracy, trapping, barcode clarity, and die-cut alignment before approval.",
    points: [
      "File cleanup and print-readiness checks",
      "Color, bleed, and registration adjustments",
      "Digital proof confirmation before press",
    ],
  },
  {
    id: "03",
    title: "Press Setup & Controlled Printing",
    tag: "Production",
    description:
      "Our team calibrates the press, substrate tension, and color targets to maintain repeatable output across the full run.",
    points: [
      "Press settings matched to job specifications",
      "Color consistency across the production batch",
      "Stable execution for high-speed output",
    ],
  },
  {
    id: "04",
    title: "Inspection & Quality Release",
    tag: "Quality",
    description:
      "Each batch passes structured checks for adhesion, print sharpness, finish quality, and die-cut accuracy before dispatch.",
    points: [
      "Batch-level print and finish verification",
      "Adhesion and visual consistency checks",
      "Final approval before packing",
    ],
  },
  {
    id: "05",
    title: "Packing & Dispatch Coordination",
    tag: "Delivery",
    description:
      "Orders are packed for safe transit and scheduled for dispatch with clear coordination to support repeat supply timelines.",
    points: [
      "Protected packing for transport safety",
      "Delivery planning based on urgency",
      "Reliable movement toward dispatch deadlines",
    ],
  },
];

/*
const quickStats = [
  { value: "05", label: "Structured production stages" },
  { value: "24-48h", label: "Express handling for urgent jobs" },
  { value: "99.9%", label: "Dispatch QC pass discipline" },
];

const workflowSignals = [
  "Material-first job planning",
  "Prepress corrections before machine time",
  "Consistent color and finish monitoring",
  "Inspection-led dispatch release",
];

const deliveryMetrics = [
  {
    title: "Clarity in planning",
    description:
      "The process begins with commercial clarity, so lead time, material route, and print requirements are aligned early.",
  },
  {
    title: "Control in production",
    description:
      "Every stage is designed to reduce variation, protect color consistency, and keep execution commercially efficient.",
  },
  {
    title: "Confidence in dispatch",
    description:
      "Final review and secure packing ensure the finished labels move out in a condition ready for application.",
  },
];
*/

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.7 },
};

const OurProcess = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="Our Printing Process | Sahajink Manufacturing Workflow"
        description="Explore our step-by-step label printing process. From artwork engineering to press setup and QC inspection, we ensure quality at every stage."
        keywords="label printing process, flexo printing workflow, printing inspection surat, label manufacturing steps, sahajink production"
      />
      <main className="min-h-screen overflow-x-hidden bg-[#07111f] text-white">
        {/* Hero Section - Centered & Professional */}
        <section className="relative overflow-hidden pt-[160px] pb-24 md:pt-[200px] md:pb-32 lg:pt-[220px]">
          {/* Cleaner Gradient Background */}
          <div className="absolute inset-0 bg-[#0a0f1a]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(37,99,235,0.12),transparent_50%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:40px_40px] opacity-40" />

          <div className="relative z-10 mx-auto max-w-[1200px] px-6">
            <div className="flex flex-col items-center text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-[950px]"
              >
                <div className="mb-8 inline-flex items-center gap-3 rounded-full border border-white/5 bg-white/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]" />
                  Workflow Architecture
                </div>

                <h1 className="mb-8 text-[34px] sm:text-[44px] font-extrabold leading-[1.2] sm:leading-[1.05] sm:tracking-tight text-white md:text-[68px] lg:text-[78px]">
                  Consistent Quality
                  <span className="sm:block bg-gradient-to-b from-white to-white/40 bg-clip-text text-transparent ml-2 sm:ml-0">
                    in Every Reel.
                  </span>
                </h1>

                <p className="mx-auto mb-12 max-w-[700px] text-[14px] sm:text-lg leading-relaxed text-white/50 md:text-[18px]">
                  Sahajink operates a production-first manufacturing workflow
                  designed exclusively for industrial reliability and
                  color-critical labeling.
                </p>

                <div className="flex flex-wrap justify-center gap-6">
                  <Link
                    to="/#contact"
                    className="rounded-full bg-blue-600 px-8 sm:px-10 py-4 sm:py-5 text-[12px] sm:text-sm font-bold uppercase tracking-widest text-white transition-all hover:bg-blue-500"
                  >
                    Discuss Requirement
                  </Link>
                  <Link
                    to="/our-works"
                    className="rounded-full border border-white/10 bg-white/5 px-8 sm:px-10 py-4 sm:py-5 text-[12px] sm:text-sm font-bold uppercase tracking-widest text-white transition-all hover:bg-white/10"
                  >
                    View Work
                  </Link>
                </div>
              </motion.div>

              {/* Metrics Row */}
              {/* <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mt-28 w-full border-t border-white/5 pt-12"
              >
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
                  {quickStats.map((item, index) => (
                    <div key={item.label} className="flex flex-col items-center">
                      <div className="text-4xl font-black text-white">
                        {item.value}
                      </div>
                      <div className="mt-3 text-[10px] font-black uppercase tracking-[0.3em] text-white/30">
                        {item.label}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div> */}
            </div>
          </div>
        </section>

        <section className="relative bg-[linear-gradient(180deg,#f8fbff_0%,#eef4ff_100%)] py-20 md:py-24">
          <div className="mx-auto max-w-[1200px] px-6">
            <motion.div {...fadeUp} className="mb-14 max-w-[720px]">
              <div className="mb-4 inline-flex rounded-full border border-blue-200 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.25em] text-blue-700">
                Workflow Structure
              </div>
              <h2 className="text-3xl font-extrabold text-slate-900 md:text-[50px] md:leading-[1.15]">
                A process page that explains how quality actually moves.
              </h2>
            </motion.div>

            <div className="relative">
              <div className="absolute left-6 top-4 hidden h-[calc(100%-2rem)] w-px bg-gradient-to-b from-blue-200 via-blue-300 to-cyan-200 md:block" />

              <div className="space-y-6">
                {processSteps.map((step, index) => (
                  <motion.article
                    key={step.id}
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.65, delay: index * 0.08 }}
                    viewport={{ once: true, amount: 0.2 }}
                    className="relative grid gap-5 rounded-[32px] border border-blue-100 bg-white p-6 shadow-[0_24px_70px_rgba(37,99,235,0.08)] md:grid-cols-[120px_1fr_0.9fr] md:gap-8 md:p-8"
                  >
                    <div className="relative flex items-start md:justify-center">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-950 text-base font-black tracking-[0.18em] text-white shadow-[0_18px_40px_rgba(15,23,42,0.2)]">
                        {step.id}
                      </div>
                    </div>

                    <div>
                      <div className="mb-3 inline-flex rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.24em] text-blue-700">
                        {step.tag}
                      </div>
                      <h3 className="text-2xl font-bold text-slate-900 md:text-[30px]">
                        {step.title}
                      </h3>
                      <p className="mt-4 max-w-[560px] text-sm leading-7 text-slate-600 md:text-base">
                        {step.description}
                      </p>
                    </div>

                    <div className="rounded-[26px] border border-slate-200 bg-slate-50 p-5">
                      <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">
                        Stage Outcomes
                      </p>
                      <div className="mt-4 space-y-3">
                        {step.points.map((point) => (
                          <div key={point} className="flex items-start gap-3">
                            <div className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-blue-500 shadow-[0_0_12px_rgba(59,130,246,0.65)]" />
                            <p className="text-sm leading-7 text-slate-600">
                              {point}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* <section className="relative overflow-hidden py-20 md:py-24 lg:py-28">
        <div className="absolute left-0 top-12 h-56 w-56 rounded-full bg-blue-500/10 blur-[120px]" />
        <div className="absolute right-0 bottom-0 h-64 w-64 rounded-full bg-cyan-400/10 blur-[140px]" />

        <div className="relative z-10 mx-auto max-w-[1200px] px-6">
          <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <motion.div {...fadeUp}>
              <div className="mb-4 inline-flex rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.25em] text-white/70">
                Process Signals
              </div>
              <h2 className="mb-6 text-3xl font-extrabold tracking-tight text-white md:text-5xl">
                The system is simple to understand and hard to break.
              </h2>
              <p className="max-w-[560px] text-sm leading-7 text-white/65 md:text-base">
                This workflow is built to reduce avoidable errors before they
                reach the machine floor. That keeps production stable, quality
                visible, and delivery commitments easier to maintain.
              </p>

              <div className="mt-8 grid gap-4">
                {workflowSignals.map((item, index) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.55, delay: index * 0.08 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-4 rounded-[22px] border border-white/10 bg-white/5 px-5 py-5"
                  >
                    <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-500/15 text-blue-200">
                      <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4 fill-none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 12.5 9 16.5 19 7.5"
                        />
                      </svg>
                    </div>
                    <p className="text-sm leading-7 text-white/75">{item}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <div className="grid gap-5">
              {deliveryMetrics.map((item, index) => (
                <motion.article
                  key={item.title}
                  initial={{ opacity: 0, y: 26 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.65, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))] p-7 shadow-[0_24px_80px_rgba(3,8,20,0.3)]"
                >
                  <div className="mb-4 flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-base font-black text-white">
                      0{index + 1}
                    </div>
                    <h3 className="text-2xl font-bold text-white">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-sm leading-7 text-white/65 md:text-base">
                    {item.description}
                  </p>
                </motion.article>
              ))}
            </div>
          </div>
        </div>
      </section> */}

        <section className="border-t border-white/10 py-20 md:py-24">
          <motion.div
            {...fadeUp}
            className="mx-auto max-w-[980px] px-6 text-center"
          >
            <div className="mb-5 inline-flex rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.25em] text-white/70">
              Move Forward
            </div>
            <h2 className="mx-auto mb-6 max-w-[780px] text-4xl font-extrabold tracking-tight text-white md:text-6xl">
              Reliability as a Service.
            </h2>
            <p className="mx-auto mb-10 max-w-[700px] text-sm leading-8 text-white/65 md:text-lg">
              Share your label requirement and we will map the right material,
              artwork flow, production route, and delivery plan for your next
              order.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/#contact"
                className="rounded-full bg-white px-8 py-4 text-[12px] sm:text-sm font-bold uppercase tracking-[0.18em] text-[#07111f] transition duration-300 hover:scale-[1.02] hover:bg-blue-200"
              >
                Discuss Your Requirement
              </Link>
              <Link
                to="/"
                className="rounded-full border border-white/15 bg-white/5 px-8 py-4 text-[12px] sm:text-sm font-semibold uppercase tracking-[0.18em] text-white transition duration-300 hover:bg-white/10"
              >
                Back To Home
              </Link>
            </div>
          </motion.div>
        </section>
      </main>
    </>
  );
};

export default OurProcess;
